// useDealData.ts
import { useQueryClient } from '@tanstack/react-query'
import { useReassignAuthRecipient } from 'api/PandaDoc/useReassignAuthRecipient'
import { useReassignContactRecipient } from 'api/PandaDoc/useReassignContactRecipient'
import { SEARCH_DATA_KEY, useEntitiesFromSearch } from 'api/search'
import { getDealBatchReplaceItems } from 'context/DealsDetailsProvider/dealUpdateData'
import { getProductBatchReplaceItems } from 'context/DealsDetailsProvider/getProductRowChanges'
import { normalizeDeal as legacyNormalizeDeal } from 'context/DealsDetailsProvider/normalizeDeal'
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { Deal, NewDeal, OrganizationTableItem, PandaDocument, Product, Totals, UpdateDocumentData } from 'types'
import {
  calculateDealTotals,
  updateProductField as calcUpdateProductField,
  dedupeAndNormalizeProducts,
  monthsFor,
  normalizeProduct,
  validateDeal,
} from 'utils/dealCalculations'
import { parseInstanceFromSK } from 'utils/ids'
import { v4 as uuidv4 } from 'uuid'
import { useAllItemsByPK } from '../organizations_table/useAllItemsByPk'
import { useGetItemByPkSk } from '../organizations_table/useGetItemByPkSk'
import { useOrganizationView } from '../organizations_table/useOrganizationView'

type ProductRow = OrganizationTableItem & Product
const resolveKey = (idOrSk: string): { SK: string | null; instanceId: string | null } => {
  if (!idOrSk) return { SK: null, instanceId: null }
  const looksLikeSK = idOrSk.startsWith('PRODUCT_')
  if (looksLikeSK) {
    const iid = parseInstanceFromSK(idOrSk)
    return { SK: idOrSk, instanceId: iid }
  } else {
    // legacy: passed instanceId
    return { SK: `PRODUCT_${parseInstanceFromSK(idOrSk)}`, instanceId: parseInstanceFromSK(idOrSk) }
  }
}
const ALL_ITEMS_KEY = SEARCH_DATA_KEY

/**
 * Full useDealData return type. Every field the hook exposes, as computed
 * from a single call. Used as the shape for DealDataContext so tabs share
 * one memoized instance instead of each tab re-running the hook's memo
 * chain on mount.
 */
export type DealDataValue = ReturnType<typeof useDealDataInternal>

/**
 * Context that carries the useDealData return so all deal-page tabs
 * (agreement, review, schedule, invoicing, comments, versions,
 * heartbeat, strategy) share one instance instead of each calling
 * useDealData themselves. Without this, every tab switch triggered
 * ~15 memoized computations (product normalization, filters, parent
 * lookups) — visible as a ~1s stutter when switching tabs on deals
 * with many products.
 */
const DealDataContext = createContext<DealDataValue | null>(null)

/**
 * Provider that shares a pre-computed useDealData value via context.
 * Mount once at the deal-page layout (DealRouting) — pass in the
 * result of an existing useDealData call so we don't run the hook
 * twice per page. All child tabs then read from this shared instance.
 */
export function DealDataProvider({
  value,
  children,
}: {
  value: DealDataValue
  children: React.ReactNode
}) {
  // JSX would require .tsx; keep this file as .ts and use createElement
  // to avoid renaming + updating every import site.
  return React.createElement(DealDataContext.Provider, { value }, children)
}

/**
 * Public hook. Prefers context (shared instance from DealDataProvider)
 * — falls back to a standalone hook call if used outside the provider.
 *
 * For any component under the deal-page layout, this is a zero-cost
 * cache read. For components rendered outside that layout (rare —
 * dev tools, standalone previews), it works exactly like before.
 */
export function useDealData(PK: string, SK: string, dealId: string) {
  const ctx = useContext(DealDataContext)
  if (ctx) return ctx
  // Fallback: standalone call. Any given component's branch stays
  // stable across renders (provider is either up-tree or it isn't),
  // so rules-of-hooks is preserved.
  return useDealDataInternal(PK, SK, dealId)
}

function useDealDataInternal(PK: string, SK: string, dealId: string) {
  const queryClient = useQueryClient()
  const [isEditMode, setIsEditMode] = useState(false)
  const { mutate: reassignAuthRecipient } = useReassignAuthRecipient()
  const { mutate: reassignContactRecipient } = useReassignContactRecipient()
  const [draftRows, setDraftRows] = useState<Product[] | null>(null)

  // ───────────────────────────────────────────────────────────────────────
  // Per-PK fetch: one DDB Query returns EVERY item under this org (deal,
  // products, contacts, etc.). The deal page is self-sufficient — no
  // dependency on the global useEntitiesFromSearch cache. Hard refresh
  // → page hydrates from this single query in ~200ms.
  // ───────────────────────────────────────────────────────────────────────
  const { data: orgItems } = useAllItemsByPK<any[]>(PK)

  const orgItemsArray = useMemo<any[]>(() => (Array.isArray(orgItems) ? orgItems : []), [orgItems])

  // Derive deals / products / organization from the per-PK item list.
  // This replaces what useEntitiesFromSearch used to provide for this
  // specific org's data — and is always fresh, since it's per-route.
  const deals = useMemo<any[]>(
    () => orgItemsArray.filter(i => i?.EntityType === 'deal'),
    [orgItemsArray],
  )
  const products = useMemo<any[]>(
    () => orgItemsArray.filter(i => i?.EntityType === 'product'),
    [orgItemsArray],
  )
  const baseOrganization = useMemo<any | undefined>(
    () => orgItemsArray.find(i => i?.EntityType === 'organization'),
    [orgItemsArray],
  )

  // Parent org STILL comes from the global cache — it's a separate PK and
  // we don't want a second fetch round trip on every deal page open.
  // useOrganizationView falls back to whatever's in useEntitiesFromSearch.
  const { organizations: globalOrgs } = useEntitiesFromSearch()

  // Once we know the parent org's PK, fetch ITS items too (per-PK,
  // ~200ms). The parent's items include PRODUCT_CUSTOM_* records that
  // hold per-org margin / pricing overrides for catalog products —
  // the auto-fill behavior on the deal product editor depends on those
  // being available immediately, not waiting for /search/all.
  const parentPK = useMemo(() => {
    const parentId = orgItemsArray.find(i => i?.EntityType === 'organization')?.contractedBy?.[0]
    if (!parentId) return undefined
    // contractedBy is org_uuid; resolve to PK by looking it up in the global
    // org list (minimal payload has it). Falls back to undefined while
    // minimal is still loading on a hard refresh.
    return globalOrgs?.find(o => o?.org_uuid === parentId)?.PK
  }, [orgItemsArray, globalOrgs])

  const { data: parentOrgItems } = useAllItemsByPK<any[]>(parentPK)

  const { data, isLoading: isDealItemLoading } = useGetItemByPkSk(PK, SK)
  React.useEffect(() => {
    if (import.meta.env.DEV) console.log('data', data)
  }, [])
  const deal = useMemo<NewDeal | undefined>(() => {
    const d = ((deals as unknown) as NewDeal[] | undefined)?.find(dd => dd.deal_uuid === dealId)
    return d ? legacyNormalizeDeal(d as any) : undefined
  }, [deals, dealId])
  const organization = useMemo(() => baseOrganization, [baseOrganization])
  const parentOrg = useMemo(() => {
    if (!organization?.contractedBy?.[0]) return null
    return globalOrgs?.find(o => o.PK === organization.contractedBy[0]) ?? null
  }, [globalOrgs, organization])
  // Custom products (margin / pricing overrides) come directly from the
  // per-PK fetch of the parent org's items. Self-sufficient — no
  // dependency on /search/all populating the global custom_products array.
  const parent_custom_products = useMemo<any[]>(() => {
    if (!Array.isArray(parentOrgItems)) return []
    return parentOrgItems
      .filter(i => i?.EntityType === 'product')
      .filter(i => String(i?.SK ?? '').startsWith('PRODUCT_CUSTOM_'))
      .filter(i => !i?.isArchived)
  }, [parentOrgItems])

  // useOrganizationView is still used for whatever else the deal page
  // reads off it (existingCustomProducts is now sourced above; this
  // call covers any other fields it returns).
  const { existingCustomProducts } = useOrganizationView(parentOrg?.PK)
  const productRows = useMemo<Product[]>(() => {
    if (!Array.isArray(products) || products?.length === 0) return []
    const rows = (products ?? []).filter(p => p.deal_uuid === dealId)
    const seeded = rows.map((p: ProductRow, i) => {
      const iid = p.instanceId ?? parseInstanceFromSK(p.SK)
      const SK = p.SK?.startsWith('PRODUCT_') ? p.SK : `PRODUCT_${iid}`
      try {
        return {
          ...normalizeProduct({ ...p, SK }, 0, deal?.dealStatus),
          sortOrder: p.sortOrder ?? i,
        }
      } catch {
        // Fallback for malformed products
        return { ...p, SK, sortOrder: p.sortOrder ?? i } as Product
      }
    })

    seeded.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    return seeded as ProductRow[]
  }, [products, dealId, deal?.dealStatus])
  const productRowsStamp = useMemo(() => {
    // A stable fingerprint that changes when the underlying list is replaced/refetched.
    // Use only fields that reflect “server refresh” (SK + updatedAt/createdAt if you have it, or qty/price)
    return (productRows ?? []).map(p => `${p.SK}|${p.updatedAt ?? p.createdAt ?? ''}|${p.sortOrder ?? ''}`).join('~')
  }, [productRows])
  const hasProducts = useMemo(() => {
    if (!Array.isArray(productRows) || productRows?.length === 0) return false
    if (productRows?.length > 0) return true
    return false
  }, [productRows])
  // ---------- Cache helper ----------
  const setAllItems = useCallback(
    (updater: (prev: OrganizationTableItem) => OrganizationTableItem) => {
      queryClient.setQueryData<OrganizationTableItem>(ALL_ITEMS_KEY, prev => (prev ? updater(prev) : (prev as any)))
    },
    [queryClient]
  )
  // ---------- Mutators ----------
  const addRow = useCallback(
    (scrollRef?: React.RefObject<HTMLElement>) => {
      if (!deal) return
      const qty = monthsFor(deal.startDate, deal.endDate)
      const forDeal = (products ?? []).filter(p => p.deal_uuid === deal.deal_uuid)
      const maxOrder = forDeal.reduce((m, p) => Math.max(m, p.sortOrder ?? -1), -1)
      const uid = uuidv4()
      const SK = `PRODUCT_${uid}`
      const newRow = {
        PK: deal.PK,
        SK,
        deal_uuid: deal.deal_uuid,
        instanceId: uid,
        rowId: uid,
        sortOrder: maxOrder + 1,
        productName: '',
        qty,
        price: 0,
        billedPrice: 0,
        margin: 0,
        mediaSpend: 0,
        total: 0,
        netTotal: 0,
        status: '',
        dateRange: [deal.startDate, deal.endDate] as any,
        startDate: deal.startDate as any,
        endDate: deal.endDate as any,
        createdAt: new Date().toISOString(),
        payer: 'GRO',
        accountUsed: 'GRO',
        // 💡 helps some server/GSI filters
        // @ts-ignore – if Product type doesn't include this yet, it's harmless to add
        EntityType: 'product',
      } as Product

      setAllItems(prev => ({
        ...prev,
        products: [...(prev.products ?? []), newRow], // ← append, no normalize here
      }))
      setTimeout(() => {
        const all = queryClient.getQueryData<any>(ALL_ITEMS_KEY)
        const _forDeal = (all?.products ?? []).filter((p: any) => p.deal_uuid === deal?.deal_uuid)
      }, 0)
      setTimeout(() => scrollRef?.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }), 0)
    },
    [deal, products, setAllItems]
  )
  const addDraftRow = useCallback(() => {
    if (!deal) return

    setDraftRows(prev => {
      const base = prev ?? []
      const maxOrder = base.reduce((m, p) => Math.max(m, p.sortOrder ?? -1), -1)
      const uid = crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`

      const raw: Partial<Product> = {
        PK: deal.PK,
        SK: `PRODUCT_${uid}`,
        deal_uuid: deal.deal_uuid,
        instanceId: uid,
        rowId: uid,
        sortOrder: maxOrder + 1,

        productName: '',
        qty: 1,
        price: 0,
        margin: 0,
        payer: 'GRO',
        accountUsed: 'GRO',

        // dates
        startDate: deal.startDate as any,
        endDate: deal.endDate as any,
        dateRange: [deal.startDate, deal.endDate] as any,

        createdAt: new Date().toISOString(),
        EntityType: 'product',
        status: '',
      }

      const normalized = normalizeProduct(raw as Product)
      return [...base, normalized]
    })
  }, [deal])

  // replace current removeRow(SK: string)
  const removeRow = useCallback(
    (idOrSk: string) => {
      setAllItems(prev => {
        if (!prev) return prev
        const { SK } = resolveKey(idOrSk)
        if (!SK) return prev
        const nextProducts = (prev.products ?? []).filter(p => p.SK !== SK)

        const deals = prev.deals ?? []
        const di = deals.findIndex(d => d.deal_uuid === dealId)
        const nextDeals =
          di > -1
            ? (() => {
                const copy = deals.slice()
                copy[di] = { ...copy[di], hasProducts: nextProducts.some(p => p.deal_uuid === dealId) }
                return copy
              })()
            : deals

        return { ...prev, products: nextProducts, deals: nextDeals }
      })
    },
    [dealId, setAllItems]
  )
  const removeDraftRow = useCallback((idOrSk: string) => {
    setDraftRows(prev => {
      if (!prev) return prev
      const sk = idOrSk.startsWith('PRODUCT_') ? idOrSk : `PRODUCT_${idOrSk}`
      return prev.filter(p => p.SK !== sk && p.instanceId !== idOrSk)
    })
  }, [])

  const patchDeal = useCallback(
    (patch: Partial<NewDeal>) => {
      setAllItems(prev => {
        const arr = prev.deals ?? []
        const idx = arr.findIndex(d => d.deal_uuid === dealId)
        if (idx < 0) return prev
        const next = arr.slice()
        next[idx] = { ...next[idx], ...patch }
        return { ...prev, deals: next }
      })
    },
    [setAllItems, dealId]
  )
  // Use centralized updateProductField from dealCalculations
  const normalizeProductRow = useCallback(
    (row: Product, field: keyof Product, value: any, clientMargin = 0, extras?: Partial<Product>) => {
      return calcUpdateProductField(row, field, value, extras ?? {}, clientMargin)
    },
    []
  )

  const updateProduct = useCallback(
    (idOrSk: string, patch: Partial<Product> | ((current: Product) => Product)) => {
      setAllItems(prev => {
        const arr = prev.products ?? []
        const { SK } = resolveKey(idOrSk)
        if (!SK) return prev
        const idx = arr.findIndex(p => p.SK === SK)
        if (idx < 0) return prev
        const current = arr[idx]
        const next = typeof patch === 'function' ? (patch as any)(current) : { ...current, ...patch }
        const copy = arr.slice()
        copy[idx] = next
        return { ...prev, products: copy }
      })
    },
    [setAllItems]
  )

  const updateProductField = useCallback(
    (idOrSk: string, field: keyof Product, value: any, extras: Partial<Product> = {}, clientMargin = 0) => {
      setAllItems(prev => {
        const arr = prev.products ?? []
        const { SK } = resolveKey(idOrSk)
        if (!SK) return prev
        const idx = arr.findIndex(p => p.SK === SK)
        if (idx < 0) return prev
        const next = arr.slice()
        next[idx] = normalizeProductRow({ ...arr[idx], ...extras }, field, value, clientMargin)
        return { ...prev, products: next } // ← no dedupe here
      })
    },
    [setAllItems, normalizeProductRow]
  )

  const restoreProductRows = useCallback(
    (rows: Product[]) => {
      setAllItems(prev => {
        if (!prev) return prev
        const others = (prev.products ?? []).filter(p => p.deal_uuid !== dealId)
        return {
          ...prev,
          products: dedupeAndNormalizeProducts([...others, ...rows]), // normalize + dedupe
        }
      })
    },
    [dealId, setAllItems]
  )

  const restoreDeal = useCallback(
    (d: NewDeal | null) => {
      if (!d) return

      queryClient.setQueryData(SEARCH_DATA_KEY, (prev: any) => {
        if (!prev) return prev

        const deals = prev.deals ?? []
        const idx = deals.findIndex((dd: Deal) => dd.deal_uuid === d.deal_uuid)
        if (idx === -1) return prev

        const nextDeals = [...deals]
        nextDeals[idx] = { ...d }

        return { ...prev, deals: nextDeals }
      })
    },
    [queryClient]
  )

  const updateDealField = useCallback(
    (field: keyof NewDeal, value: any) => {
      setAllItems(prev => {
        const arr = prev.deals ?? []
        const idx = arr.findIndex(d => d.deal_uuid === dealId)
        if (idx < 0) return prev

        const next = arr.slice()
        const current = next[idx]
        const updated: any = { ...current }

        if (field === 'recipients') {
          const r = value ?? {}
          updated.recipients = {
            authorizedRecipient: r.authorizedRecipient ?? [],
            contactRecipient: r.contactRecipient ?? [],
          }
        } else {
          const ALLOW_EMPTY = new Set<keyof NewDeal>(['implementationFee', 'type', 'dba', 'stage', 'status'])

          if (value === undefined || value === null) {
            delete updated[field]
          } else if (typeof value === 'string' && value.trim() === '' && !ALLOW_EMPTY.has(field)) {
            delete updated[field]
          } else {
            updated[field] = value
          }
        }

        next[idx] = updated
        return { ...prev, deals: next }
      })
    },
    [dealId, setAllItems] // ✅ REMOVED 'deal' dependency
  )
  // ---------- Utilities (using centralized calculations) ----------
  const computeTotals = useCallback((d: NewDeal, rows: Product[]): Totals => {
    return calculateDealTotals(d, rows)
  }, [])

  const validate = useCallback((d: NewDeal | undefined, rows?: Product[]) => {
    return validateDeal(d, rows)
  }, [])

  // Keep your existing server-side save and replace cache if needed.
  const saveDeal = useCallback(async () => {
    if (!deal) return
    const totals = computeTotals(deal, productRows)
    const dealUpdates = getDealBatchReplaceItems(deal, { ...deal, totals })
    const productDiffs = getProductBatchReplaceItems(productRows, productRows)

    // await saveDealAsync({ PK: deal.PK, SK: deal.SK, dealUpdates, products: productRows, deletedProducts: [] })
    // Optionally: invalidate or patch with returned server rows
    return { totals, dealUpdates, productDiffs }
  }, [deal, productRows, computeTotals])

  const _resolveToSK = (id: string): string | null => {
    if (!id) return null
    if (id.startsWith('PRODUCT_')) return id // already SK
    const row = (products ?? []).find(p => p.instanceId === id) // legacy path
    return row?.SK ?? null
  }

  const handleProductChange = useCallback(
    (sk: string, field: keyof Product, value: any, options?: { extras?: Partial<Product>; clientMargin?: number }) => {
      updateProductField(sk, field, value, options?.extras ?? {}, options?.clientMargin ?? 0)
    },
    [updateProductField]
  )

  // index-based: pass SK
  const handleProductChangeByIndex = useCallback(
    (index, field, value, options) => {
      const row = productRows[index]
      if (!row) return
      // pass SK (new) but also accept instanceId (legacy)
      updateProductField(
        row.SK ?? `PRODUCT_${row.instanceId}`,
        field,
        value,
        options?.extras ?? {},
        options?.clientMargin ?? 0
      )
    },
    [productRows, updateProductField]
  )
  const applyProductEdit = (
    row: Product,
    field: keyof Product,
    value: any,
    opts?: { extras?: Partial<Product>; clientMargin?: number }
  ): Product => {
    const extras = opts?.extras ?? {}
    const clientMargin = opts?.clientMargin ?? 0

    // Use centralized updateProductField which handles normalization and money derivation
    return calcUpdateProductField(row, field, value, extras, clientMargin)
  }
  const handleDraftProductChangeByIndex = useCallback(
    (
      index: number,
      field: keyof Product,
      value: any,
      options?: { extras?: Partial<Product>; clientMargin?: number }
    ) => {
      setDraftRows(prev => {
        if (!prev) return prev
        const next = prev.slice()
        const row = next[index]
        if (!row) return prev

        // block edits to derived fields
        if (field === 'mediaSpend' || field === 'grossProfit' || field === 'billedPrice') {
          return prev
        }

        next[index] = applyProductEdit(row, field, value, options)
        return next
      })
    },
    []
  )
  // Move a product row in draft mode (editing)
  const moveDraftRow = useCallback((result: { source: { index: number }; destination?: { index: number } | null }) => {
    const { source, destination } = result
    if (!destination) return
    if (source.index === destination.index) return

    setDraftRows(prev => {
      if (!prev) return prev
      if (source.index < 0 || source.index >= prev.length) return prev
      if (destination.index < 0 || destination.index >= prev.length) return prev

      const reordered = prev.slice()
      const [moved] = reordered.splice(source.index, 1)
      reordered.splice(destination.index, 0, moved)

      // Update sortOrder for all rows
      return reordered.map((p, i) => ({ ...p, sortOrder: i }))
    })
  }, [])

  // Move a product row in non-editing mode (updates global state)
  const moveProductRow = useCallback(
    (result: {
      source: { index: number; droppableId: string }
      destination?: { index: number; droppableId: string } | null
    }) => {
      const { source, destination } = result
      if (!destination) return
      if (source.droppableId !== destination.droppableId) return
      if (source.index === destination.index) return

      setAllItems(prev => {
        const all = prev.products ?? []
        const dealRows = all
          .filter(p => p.deal_uuid === dealId)
          .map((p, i) => ({ ...p, sortOrder: p.sortOrder ?? i }))
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))

        if (
          source.index < 0 ||
          source.index >= dealRows.length ||
          destination.index < 0 ||
          destination.index >= dealRows.length
        )
          return prev

        const reordered = dealRows.slice()
        const [moved] = reordered.splice(source.index, 1)
        reordered.splice(destination.index, 0, moved)
        reordered.forEach((p, i) => {
          p.sortOrder = i
        })

        // apply back to all via SK (canonical)
        const orderBySk = new Map(reordered.map(p => [p.SK, p.sortOrder]))
        const next = all.map(p => (orderBySk.has(p.SK) ? { ...p, sortOrder: orderBySk.get(p.SK)! } : p))

        return { ...prev, products: next }
      })
    },
    [dealId, setAllItems]
  )

  const handleReassignAuthRecipient = async (documentData: UpdateDocumentData) => {
    return reassignAuthRecipient({ ...documentData })
  }
  const handleReassignContactRecipient = async (documentData: UpdateDocumentData) => {
    return reassignContactRecipient({ ...documentData })
  }
  const reassignRecipients = async (
    cleanedDeal: NewDeal,
    document: PandaDocument,
    documentData: UpdateDocumentData
  ) => {
    try {
      const newAuth = cleanedDeal?.recipients?.authorizedRecipient?.[0]
      const newAuthId = newAuth?.contact_id || newAuth?.panda_contact_id

      const newClient = cleanedDeal?.recipients?.contactRecipient?.[0]
      const newClientId = newClient?.contact_id || newClient?.panda_contact_id

      const oldAuth = document?.recipients?.find(r => r.role === 'Authorized Personnel')
      const oldClient = document?.recipients?.find(r => r.role === 'Client')

      if (oldAuth?.contact_id !== newAuthId) {
        await handleReassignAuthRecipient(documentData)
      }
      if (oldClient?.contact_id !== newClientId) {
        await handleReassignContactRecipient(documentData)
      }
    } catch (error) {
      console.error('[reassignRecipients] error', error)
    }
  }
  const stopEdit = () => {
    setIsEditMode(false)
  }
  const startEdit = () => {
    setIsEditMode(true)
  }
  return {
    // data
    deal,
    draftRows,
    setDraftRows,
    productRows,
    organization,
    hasProducts,
    parent_custom_products,
    // helpers
    computeTotals,
    validate,
    reassignRecipients,

    // mutations (cache-first)
    addRow,
    addDraftRow,
    removeRow,
    removeDraftRow,
    updateProduct,
    updateProductField,
    restoreProductRows,
    restoreDeal,
    updateDealField,
    handleProductChange,
    handleProductChangeByIndex,
    handleDraftProductChangeByIndex,
    moveProductRow,
    moveDraftRow,

    // persist
    saveDeal,
    productRowsStamp,
    // state
    isEditMode,
    startEdit,
    stopEdit,
    patchDeal,
  }
}
